import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";

import {
  Container,
  Row,
  Col,
  Button,
  ModalView,
  LoadingIndicator
} from "../../../components/simple";

import { ModalClientForm } from "../../../components/forms/Client/Edit";

import Header from "../../../components/complex/Header";
import ClientCard from "../../../components/complex/ClientCard";
import ModalClientUpgrade from "../../../components/complex/ModalClientUpgrade";
import SVG from "../../../components/SVG";

import { clearClientsData } from "../../../store/actions/clients";

import * as routes from "../../../router/routes";
import Api from "../../../api";

import styles from "./account-overview.module.scss";

const AccountOverview = () => {
  const user = useSelector(state => state.userProfile.user);
  const clients = useSelector(state => state.clients.data);
  const isLoading = useSelector(state => state.clients.isLoading);
  const history = useHistory();
  const dispatch = useDispatch();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const [selectedClient, setSelectedClient] = useState("");
  const [dataToEdit, setDataToEdit] = useState({});
  const [clientToUpgrade, setClientToUpgrade] = useState(null);

  useEffect(() => {
    dispatch(clearClientsData());

    const getClients = async () => {
      try {
        await Api.client.get();
      } catch (error) {
        console.log(error);
      }
    };

    getClients();
  }, [dispatch]);

  const changeModalState = () => setIsModalOpen(prevState => !prevState);

  const handleRemove = isRemove => setIsRemoveModalOpen(isRemove);

  const handleAddClient = () => {
    setDataToEdit({});
    setIsModalOpen(true);
  };

  const handleEditClient = data => {
    setIsModalOpen(true);
    setDataToEdit(data);
  };

  const handleSaveClient = async data => {
    // If data has ID that means that it already been created, and now we only modify data
    if (data.id) await Api.client.edit({ id: data.id, data });
    else await Api.client.add({ data });
    setIsModalOpen(false);
  };

  const handleRemoveClient = async data => {
    await Api.client.remove({ id: data.id });
    setIsRemoveModalOpen(false);
    setSelectedClient(null);
  };

  const handleUpgrade = async ({ id, name }) => {
    await Api.client.upgrade({ id, data: { name } });
    setIsUpgradeModalOpen(false);
  };

  const calculateClientsData = clients.reduce(
    (total, currentClient) => {
      const {
        statistics: { activations, uploads }
      } = currentClient;

      return {
        activations: activations + total.activations,
        uploads: uploads + total.uploads
      };
    },
    { activations: 0, uploads: 0 }
  );

  return (
    <Container fluid className={styles["accout-overview"]}>
      <div className={styles["header-wrapper"]}>
        <Container>
          <Row>
            <Container className={styles["header-container"]}>
              <Header />
            </Container>
          </Row>
          <Row className={styles["header-overview"]}>
            <Container>
              <Row className={styles["header-dashboard"]}>
                <Col>
                  <h1 className={`title ${styles["header-dashboard__title"]}`}>
                    Account overview
                  </h1>
                  <p className="description">
                    One overview of all clients connected to your account
                  </p>
                </Col>
              </Row>
              {isLoading ? (
                <div
                  style={{
                    height: "96px",
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center"
                  }}
                >
                  <LoadingIndicator />
                </div>
              ) : (
                <Row className={styles["header-statistics"]}>
                  <Col className={styles["header-statistics__items"]}>
                    <div className={styles["header-statistics__item"]}>
                      <span className={styles["header-statistics__number"]}>
                        {/* sandbox isn't client */}
                        {clients.length === 0 ? 0 : clients.length - 1}
                      </span>
                      Clients
                    </div>
                    <div className={styles["header-statistics__item"]}>
                      <span className={styles["header-statistics__number"]}>
                        {calculateClientsData.activations}
                      </span>
                      Activations
                    </div>
                    <div className={styles["header-statistics__item"]}>
                      <span className={styles["header-statistics__number"]}>
                        {calculateClientsData.uploads}
                      </span>
                      Uploads
                    </div>
                  </Col>
                </Row>
              )}
              <Row>
                <Col>
                  <p className={styles.description}>Total Monthly activities</p>
                </Col>
              </Row>
            </Container>
          </Row>
        </Container>
      </div>
      <Row className={styles.clients}>
        <Container>
          <Row className="align-items-end">
            <Col>
              <h2 className="container-title">Clients</h2>
              <p className={styles.description}>Activities current Month</p>
            </Col>
            <Col className="align-self-end">
              <Button
                type="button"
                className="button ml-auto"
                onClick={handleAddClient}
              >
                <span>Add Client</span>
                <SVG.Plus className="icon" />
              </Button>
            </Col>
          </Row>
          <Row className="mt-5">
            {!isLoading ? (
              clients?.map(client => (
                <Col md="4" style={{ marginBottom: "30px" }}>
                  <ClientCard
                    key={client.id}
                    isSandbox={client.sandbox}
                    handleClick={id => {
                      dispatch(clearClientsData());
                      history.push(routes.clients(id));
                    }}
                    canEdit={
                      user?.role === "admin" || user?.role === "gdr admin"
                    }
                    canRemove={
                      user?.role === "admin" || user?.role === "gdr admin"
                    }
                    handleEdit={data => handleEditClient(data)}
                    handleRemove={() => {
                      setIsRemoveModalOpen(true);
                      setSelectedClient(client.id);
                    }}
                    handleClickUpgradeBtn={() => {
                      setIsUpgradeModalOpen(true);
                      setClientToUpgrade(client);
                    }}
                    data={client}
                  />
                </Col>
              ))
            ) : (
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center"
                }}
              >
                <LoadingIndicator />
              </div>
            )}
          </Row>
        </Container>
      </Row>
      <ModalClientForm
        isOpen={isModalOpen}
        changeModalState={changeModalState}
        data={dataToEdit}
        handleSubmit={handleSaveClient}
      />
      <ModalView
        isOpen={isRemoveModalOpen}
        changeModalState={() => {
          handleRemove(isRemoveModalOpen);
        }}
        handleClose={() => setIsRemoveModalOpen(false)}
      >
        <h2 className="title text-center mb-3">Delete Client </h2>
        <p className="description mb-4" style={{ textAlign: "center" }}>
          Are you sure you want to delete the client?
        </p>
        <div className="buttons-container">
          <Button
            onClick={() => handleRemoveClient({ id: selectedClient })}
            className="button button-danger justify-content-center ml-auto mr-auto"
          >
            Delete Forever
          </Button>
        </div>
      </ModalView>
      <ModalClientUpgrade
        isOpen={isUpgradeModalOpen}
        changeModalState={() => {
          setIsUpgradeModalOpen(prevState => !prevState);
        }}
        handleClose={() => setIsUpgradeModalOpen(false)}
        handleUpgrade={() => handleUpgrade(clientToUpgrade)}
      />
    </Container>
  );
};

export default AccountOverview;
